import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar, Th, TenancyModal, Toggle } from '../platform/_ui';
import { orgDeveloperApi } from '../api/org-developer.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';

const TABS = [
  { id: 'apikeys',      label: 'API Keys',      icon: 'Key'        },
  { id: 'oauthapps',   label: 'OAuth Apps',    icon: 'Lock'       },
  { id: 'webhooks',     label: 'Webhooks',      icon: 'Zap'        },
  { id: 'secrets',      label: 'Secrets',       icon: 'EyeOff'     },
  { id: 'externalapps', label: 'External Apps', icon: 'Globe'      },
  { id: 'integrations', label: 'Integrations',  icon: 'Plug'       },
  { id: 'ratelimits',   label: 'Rate Limits',   icon: 'Gauge'      },
  { id: 'logs',         label: 'API Logs',      icon: 'FileText'   },
  { id: 'sdk',          label: 'SDK & Docs',    icon: 'Code'       },
];

const SCOPES    = ['read:crm', 'write:crm', 'read:analytics', 'read:members', 'write:members', 'admin:org'];
const WH_EVENTS = ['contact.created', 'contact.updated', 'deal.created', 'deal.stage_changed', 'task.created', 'member.invited', 'workspace.created'];
/* C-05: No integration setup is implemented. All entries are coming_soon until
   OAuth / webhook flows are built and certified. Status must not read "Available"
   for features that show "coming soon" on click. */
const INTEGRATIONS = [
  { id: 'zapier',     name: 'Zapier',     icon: 'Zap',       desc: 'Automate workflows across 5000+ apps.', status: 'coming_soon' },
  { id: 'make',       name: 'Make',       icon: 'GitMerge',  desc: 'Visual automation platform.',           status: 'coming_soon' },
  { id: 'n8n',        name: 'n8n',        icon: 'Network',   desc: 'Open-source workflow automation.',      status: 'coming_soon' },
  { id: 'hubspot',    name: 'HubSpot',    icon: 'Globe',     desc: 'Sync contacts with HubSpot CRM.',      status: 'coming_soon' },
  { id: 'salesforce', name: 'Salesforce', icon: 'Cloud',     desc: 'Enterprise CRM sync.',                 status: 'coming_soon' },
  { id: 'pipedrive',  name: 'Pipedrive',  icon: 'GitBranch', desc: 'Sales pipeline integration.',          status: 'coming_soon' },
];

const RATE_LIMITS_STATIC = [
  { tier: 'Read',     limit: '1,000 req/min', burst: '2,000', methods: 'GET'           },
  { tier: 'Write',    limit: '200 req/min',   burst: '400',   methods: 'POST, PATCH'   },
  { tier: 'Delete',   limit: '50 req/min',    burst: '100',   methods: 'DELETE'        },
  { tier: 'Webhooks', limit: '500 events/min',burst: '—',     methods: 'Outbound only' },
  { tier: 'Bulk',     limit: '10 req/min',    burst: '20',    methods: 'POST /bulk/*'  },
];

const EXTERNAL_SCOPE_GROUPS = [
  { resource: 'People',         read: 'crm.people.read',          write: 'crm.people.write'          },
  { resource: 'Companies',      read: 'crm.companies.read',       write: 'crm.companies.write'       },
  { resource: 'Deals',          read: 'crm.deals.read',           write: 'crm.deals.write'           },
  { resource: 'Tasks',          read: 'crm.tasks.read',           write: 'crm.tasks.write'           },
  { resource: 'Notes',          read: 'crm.notes.read',           write: 'crm.notes.write'           },
  { resource: 'Activities',     read: 'crm.activities.read',      write: 'crm.activities.write'      },
  { resource: 'Custom Objects', read: 'crm.custom-objects.read',  write: 'crm.custom-objects.write'  },
];

const extStatusTone = (s) => s === 'ACTIVE' ? 'green' : s === 'SUSPENDED' ? 'amber' : 'red';
const isValidUrl    = (url) => { try { new URL(url); return true; } catch { return false; } };
const mask = (k) => k ? k.slice(0, 8) + '•'.repeat(24) + k.slice(-6) : '••••••••••••••••••••••••••••••••';
const fmt  = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ── Expiry Date Picker ───────────────────────────────────────────────────────
const MONTHS_LONG   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAY_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function parseDateLocal(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function ExpiryDatePicker({ value, onChange }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const selDate = parseDateLocal(value);

  const [open, setOpen] = useState(false);
  const [vy, setVy] = useState(() => (selDate || today).getFullYear());
  const [vm, setVm] = useState(() => (selDate || today).getMonth());
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const atMin = vy === today.getFullYear() && vm === today.getMonth();

  const goNext = () => {
    if (vm === 11) { setVy(y => y + 1); setVm(0); } else setVm(m => m + 1);
  };
  const goPrev = () => {
    if (atMin) return;
    if (vm === 0) { setVy(y => y - 1); setVm(11); } else setVm(m => m - 1);
  };

  const openPicker = () => {
    const ref = selDate || today;
    setVy(ref.getFullYear()); setVm(ref.getMonth()); setOpen(true);
  };

  const pick = (day) => {
    const d = new Date(vy, vm, day); d.setHours(0, 0, 0, 0);
    if (d < today) return;
    onChange(`${vy}-${String(vm + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    setOpen(false);
  };

  const firstDow  = new Date(vy, vm, 1).getDay();
  const daysInMo  = new Date(vy, vm + 1, 0).getDate();
  const displayVal = selDate
    ? selDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {/* Trigger field */}
      <div
        className="nx-input"
        role="combobox"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => open ? setOpen(false) : openPicker()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open ? setOpen(false) : openPicker(); } }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', minHeight: 36, padding: '0 10px' }}
      >
        <span style={{ flex: 1, fontSize: 13, color: displayVal ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}>
          {displayVal || 'No expiry'}
        </span>
        {value && (
          <button
            type="button"
            aria-label="Clear expiry date"
            onClick={e => { e.stopPropagation(); onChange(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: '0 2px', lineHeight: 1, fontSize: 16 }}
          >×</button>
        )}
        <NexusIcon name="Calendar" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
      </div>

      {/* Calendar popup */}
      {open && (
        <div
          role="dialog"
          aria-label="Pick expiry date"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999,
            background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
            borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.14)', padding: '14px 12px', width: 260,
          }}
        >
          {/* Month/year header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={goPrev} disabled={atMin} aria-label="Previous month"
              style={{ background: 'none', border: 'none', cursor: atMin ? 'default' : 'pointer', borderRadius: 6,
                       color: atMin ? 'var(--nx-border)' : 'var(--nx-text)', padding: '4px 10px', fontSize: 18, lineHeight: 1,
                       opacity: atMin ? 0.3 : 1 }}>‹</button>
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--nx-text)' }}>
              {MONTHS_LONG[vm]} {vy}
            </span>
            <button type="button" onClick={goNext} aria-label="Next month"
              style={{ background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6,
                       color: 'var(--nx-text)', padding: '4px 10px', fontSize: 18, lineHeight: 1 }}>›</button>
          </div>

          {/* Weekday labels */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {WEEKDAY_SHORT.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700,
                                    color: 'var(--nx-text-muted)', padding: '2px 0',
                                    textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {Array.from({ length: firstDow }).map((_, i) => <div key={`p${i}`} />)}
            {Array.from({ length: daysInMo }, (_, i) => i + 1).map(day => {
              const d = new Date(vy, vm, day); d.setHours(0, 0, 0, 0);
              const past   = d < today;
              const isToday = d.getTime() === today.getTime();
              const isSel   = selDate && d.getTime() === selDate.getTime();
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => pick(day)}
                  disabled={past}
                  aria-label={`${MONTHS_LONG[vm]} ${day}, ${vy}${past ? ' (unavailable)' : ''}`}
                  aria-pressed={isSel || undefined}
                  style={{
                    border: isToday && !isSel ? '1px solid var(--nx-accent)' : '1px solid transparent',
                    borderRadius: 6, cursor: past ? 'default' : 'pointer',
                    fontSize: 12, fontWeight: isSel || isToday ? 600 : 400,
                    padding: '5px 0', textAlign: 'center',
                    background: isSel ? 'var(--nx-accent)' : 'transparent',
                    color: isSel ? '#fff' : past ? 'var(--nx-border)' : 'var(--nx-text)',
                    opacity: past ? 0.38 : 1,
                    outline: 'none', transition: 'background 0.1s',
                  }}
                  onFocus={e => { if (!past && !isSel) e.currentTarget.style.outline = '2px solid var(--nx-accent)'; }}
                  onBlur={e => { e.currentTarget.style.outline = 'none'; }}
                  onMouseEnter={e => { if (!past && !isSel) e.currentTarget.style.background = 'var(--nx-accent-tint)'; }}
                  onMouseLeave={e => { if (!past && !isSel) e.currentTarget.style.background = 'transparent'; }}
                >{day}</button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--nx-border)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button"
              onClick={() => { setVy(today.getFullYear()); setVm(today.getMonth()); pick(today.getDate()); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
                       color: 'var(--nx-accent)', padding: '2px 4px', borderRadius: 4 }}>
              Today
            </button>
            {value && (
              <button type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
                         color: 'var(--nx-text-muted)', padding: '2px 4px', borderRadius: 4 }}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────

export default function OrgDeveloper({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast = useToast();
  const perm  = usePermissions();

  const [tab, setTab]                 = useState('apikeys');
  const [apiKeys, setApiKeys]         = useState([]);
  const [oauthApps, setOauthApps]     = useState([]);
  const [webhooks, setWebhooks]       = useState([]);
  const [secrets, setSecrets]         = useState([]);
  const [logs, setLogs]               = useState([]);
  const [showKey, setShowKey]         = useState(null);
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [rotateConfirm, setRotateConfirm] = useState(null);
  const [form, setForm] = useState({ name: '', scopes: [], url: '', events: [], value: '', description: '' });
  const [newAppCreds, setNewAppCreds] = useState(null);
  const [whDeliveries, setWhDeliveries] = useState(null);

  // External Apps state
  const [extApps, setExtApps]                   = useState([]);
  const [extAppsLoading, setExtAppsLoading]     = useState(false);
  const [selectedApp, setSelectedApp]           = useState(null);
  const [extCreds, setExtCreds]                 = useState([]);
  const [extGrants, setExtGrants]               = useState([]);
  const [extDetailLoading, setExtDetailLoading] = useState(false);
  const [newExtApiKey, setNewExtApiKey]         = useState(null);
  const [extModal, setExtModal]                 = useState(null);
  const [extConfirm, setExtConfirm]             = useState(null);
  const [extForm, setExtForm] = useState({ name: '', description: '', credName: '', credExpiry: '', wsId: '', scopes: [] });
  const [editScopesGrant, setEditScopesGrant]   = useState(null);

  const loadKeys = useCallback(async () => {
    try { setApiKeys((await orgDeveloperApi.listKeys(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadApps = useCallback(async () => {
    try { setOauthApps((await orgDeveloperApi.listOAuthApps(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadWebhooks = useCallback(async () => {
    try { setWebhooks((await orgDeveloperApi.listWebhooks(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadSecrets = useCallback(async () => {
    try { setSecrets((await orgDeveloperApi.listSecrets(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadLogs = useCallback(async () => {
    try { setLogs((await orgDeveloperApi.getLogs(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadExtApps = useCallback(async () => {
    setExtAppsLoading(true);
    try { setExtApps((await orgDeveloperApi.listExternalApps(orgId)) || []); }
    catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load External Apps' }); }
    finally { setExtAppsLoading(false); }
  }, [orgId]);

  const loadExtDetail = useCallback(async (app) => {
    setExtDetailLoading(true);
    try {
      const [creds, grants] = await Promise.all([
        orgDeveloperApi.listExternalCredentials(orgId, app.id),
        orgDeveloperApi.listWorkspaceGrants(orgId, app.id),
      ]);
      setExtCreds(creds || []);
      setExtGrants(grants || []);
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load app details' }); }
    finally { setExtDetailLoading(false); }
  }, [orgId]);

  useEffect(() => { loadKeys(); loadApps(); loadWebhooks(); loadSecrets(); }, [loadKeys, loadApps, loadWebhooks, loadSecrets]);
  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);
  useEffect(() => { if (tab === 'externalapps') loadExtApps(); }, [tab, loadExtApps]);
  useEffect(() => { if (selectedApp) loadExtDetail(selectedApp); }, [selectedApp, loadExtDetail]);

  const org = data.state.organizations.find(o => o.id === orgId);
  if (!org) return null;

  const isAdmin = perm.has('developer.api-keys.view');
  if (!isAdmin) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="Developer Center requires Developer or Admin access. Contact your org admin to request the developer.api-keys.view capability."
      />
    </div>
  );

  const resetForm   = () => setForm({ name: '', scopes: [], url: '', events: [], value: '', description: '' });
  const toggleScope = (s)  => setForm(f => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s] }));
  const toggleEvent = (ev) => setForm(f => ({ ...f, events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev] }));

  const handleCreateKey = async () => {
    if (!form.name.trim()) return;
    try {
      const created = await orgDeveloperApi.generateKey(orgId, { name: form.name.trim(), scopes: form.scopes });
      setNewKeyValue(created.rawKey || created.key || created.value || '');
      await loadKeys();
      setCreateModal(null); resetForm();
      toast({ tone: 'success', icon: 'Key', title: 'API Key created', desc: 'Copy the key now — it will not be shown again.' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create API key', desc: e.message }); }
  };

  const handleCreateApp = async () => {
    if (!form.name.trim()) return;
    try {
      const created = await orgDeveloperApi.createOAuthApp(orgId, { name: form.name.trim(), scopes: form.scopes });
      const clientId     = created?.clientId     || created?.client_id     || '';
      const clientSecret = created?.clientSecret || created?.client_secret || '';
      if (clientId || clientSecret) setNewAppCreds({ clientId, clientSecret });
      await loadApps();
      setCreateModal(null); resetForm();
      toast({ tone: 'success', icon: 'Lock', title: 'OAuth App created', desc: clientSecret ? 'Copy the client secret now.' : '' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create OAuth app', desc: e.message }); }
  };

  const handleCreateWebhook = async () => {
    if (!form.name.trim()) return;
    if (!form.url.trim() || !isValidUrl(form.url.trim())) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Must be a valid URL', desc: 'Enter a full URL starting with https://' });
      return;
    }
    try {
      await orgDeveloperApi.createWebhook(orgId, { name: form.name.trim(), url: form.url.trim(), events: form.events });
      await loadWebhooks();
      setCreateModal(null); resetForm();
      toast({ tone: 'success', icon: 'Zap', title: 'Webhook created' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create webhook', desc: e.message }); }
  };

  const handleCreateSecret = async () => {
    if (!form.name.trim() || !form.value.trim()) return;
    try {
      await orgDeveloperApi.createSecret(orgId, { name: form.name.trim(), value: form.value, description: form.description });
      await loadSecrets();
      setCreateModal(null); resetForm();
      toast({ tone: 'success', icon: 'EyeOff', title: 'Secret stored' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to store secret', desc: e.message }); }
  };

  const handleRotate = async (key) => {
    try {
      const result = await orgDeveloperApi.rotateKey(orgId, key.id);
      setNewKeyValue(result.rawKey || result.key || result.value || '');
      await loadKeys();
      toast({ tone: 'success', icon: 'RefreshCw', title: 'Key rotated', desc: 'Copy the new key now.' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to rotate key', desc: e.message }); }
  };

  const handleRevoke = async () => {
    const { id, type } = deleteConfirm;
    try {
      if (type === 'key')     { await orgDeveloperApi.revokeKey(orgId, id);      await loadKeys();     }
      if (type === 'app')     { await orgDeveloperApi.deleteOAuthApp(orgId, id); await loadApps();     }
      if (type === 'webhook') { await orgDeveloperApi.deleteWebhook(orgId, id);  await loadWebhooks(); }
      if (type === 'secret')  { await orgDeveloperApi.deleteSecret(orgId, id);   await loadSecrets();  }
      toast({ tone: 'warning', icon: 'Trash', title: 'Revoked' });
      setDeleteConfirm(null);
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to revoke', desc: e.message }); }
  };

  const testWebhook = async (wh) => {
    try {
      await orgDeveloperApi.testWebhook(orgId, wh.id);
      toast({ tone: 'success', icon: 'Send', title: 'Test event sent', desc: 'Check your endpoint for the delivery.' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Test delivery failed', desc: e.message }); }
  };

  const toggleWebhookStatus = async (wh) => {
    try {
      await orgDeveloperApi.updateWebhook(orgId, wh.id, { active: !wh.active });
      await loadWebhooks();
      toast({ tone: 'success', icon: 'Zap', title: wh.active ? 'Webhook disabled' : 'Webhook enabled' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update webhook', desc: e.message }); }
  };

  const viewDeliveries = async (wh) => {
    try {
      const deliveryLogs = await orgDeveloperApi.getWebhookDeliveries(orgId, wh.id);
      setWhDeliveries({ webhook: wh, logs: deliveryLogs || [] });
    } catch { setWhDeliveries({ webhook: wh, logs: [] }); }
  };

  // External Apps helpers
  const orgWorkspaces  = (data.state.workspaces || []).filter(w => w.org === orgId && w.status === 'ACTIVE');
  const resetExtForm   = () => setExtForm({ name: '', description: '', credName: '', credExpiry: '', wsId: '', scopes: [] });
  const toggleExtScope = (s) => setExtForm(f => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s] }));

  const handleCreateExtApp = async () => {
    if (!extForm.name.trim()) return;
    try {
      await orgDeveloperApi.createExternalApp(orgId, { name: extForm.name.trim(), description: extForm.description.trim() || undefined });
      await loadExtApps();
      setExtModal(null); resetExtForm();
      toast({ tone: 'success', icon: 'Globe', title: 'External application created' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create application', desc: e.message }); }
  };

  const handleCreateExtCred = async () => {
    if (!extForm.credName.trim() || !selectedApp) return;
    if (extForm.credExpiry) {
      const expiry = parseDateLocal(extForm.credExpiry);
      const tod = new Date(); tod.setHours(0, 0, 0, 0);
      if (expiry && expiry < tod) {
        toast({ tone: 'error', icon: 'AlertCircle', title: 'Invalid expiry date', desc: 'Expiry must be today or a future date.' });
        return;
      }
    }
    try {
      const result = await orgDeveloperApi.createExternalCredential(orgId, selectedApp.id, {
        name: extForm.credName.trim(),
        expiresAt: extForm.credExpiry || undefined,
      });
      // Raw key held only in short-lived local state; cleared when dialog closes
      setNewExtApiKey(result.apiKey || '');
      await loadExtDetail(selectedApp);
      setExtModal(null); resetExtForm();
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create credential', desc: e.message }); }
  };

  const handleGrantWorkspace = async () => {
    if (!extForm.wsId || extForm.scopes.length === 0 || !selectedApp) return;
    try {
      await orgDeveloperApi.grantWorkspace(orgId, selectedApp.id, { workspaceId: extForm.wsId, scopes: extForm.scopes });
      await loadExtDetail(selectedApp);
      setExtModal(null); resetExtForm();
      toast({ tone: 'success', icon: 'Globe', title: 'Workspace access granted' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to grant workspace access', desc: e.message }); }
  };

  const handleUpdateScopes = async () => {
    if (!editScopesGrant || extForm.scopes.length === 0 || !selectedApp) return;
    try {
      await orgDeveloperApi.updateWorkspaceGrant(orgId, selectedApp.id, editScopesGrant.workspaceId, { scopes: extForm.scopes });
      await loadExtDetail(selectedApp);
      setEditScopesGrant(null); resetExtForm();
      toast({ tone: 'success', icon: 'Check', title: 'Scopes updated' });
    } catch (e) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update scopes', desc: e.message }); }
  };

  const handleExtConfirm = async () => {
    if (!extConfirm) return;
    try {
      if (extConfirm.type === 'revokeCredential') {
        await orgDeveloperApi.revokeExternalCredential(orgId, selectedApp.id, extConfirm.id);
        await loadExtDetail(selectedApp);
        toast({ tone: 'warning', icon: 'X', title: 'Credential revoked' });
      }
      if (extConfirm.type === 'revokeGrant') {
        await orgDeveloperApi.revokeWorkspaceGrant(orgId, selectedApp.id, extConfirm.workspaceId);
        await loadExtDetail(selectedApp);
        toast({ tone: 'warning', icon: 'X', title: 'Workspace access revoked' });
      }
      if (extConfirm.type === 'changeAppStatus') {
        const updated = await orgDeveloperApi.updateExternalApp(orgId, selectedApp.id, { status: extConfirm.status });
        setSelectedApp(prev => ({ ...prev, status: updated.status || extConfirm.status }));
        await loadExtApps();
        const label = extConfirm.status === 'ACTIVE' ? 'reactivated' : extConfirm.status === 'SUSPENDED' ? 'suspended' : 'revoked';
        toast({ tone: extConfirm.status === 'ACTIVE' ? 'success' : 'warning', icon: extConfirm.status === 'ACTIVE' ? 'Check' : 'PauseCircle', title: 'Application ' + label });
      }
      setExtConfirm(null);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: e.message });
      setExtConfirm(null);
    }
  };

  const ScopeCheckboxes = ({ selected, onToggle }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
      {EXTERNAL_SCOPE_GROUPS.map(g => (
        <div key={g.resource}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{g.resource}</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', marginBottom: 3 }}>
            <input type="checkbox" checked={selected.includes(g.read)}  onChange={() => onToggle(g.read)}  style={{ accentColor: 'var(--nx-accent)' }} /> read
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={selected.includes(g.write)} onChange={() => onToggle(g.write)} style={{ accentColor: 'var(--nx-accent)' }} /> write
          </label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Developer Center"
        sub={'API access and integrations for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={
          tab === 'apikeys'   ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('key');     }}>Generate API Key</Btn>  :
          tab === 'oauthapps' ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('app');     }}>Create OAuth App</Btn>  :
          tab === 'webhooks'  ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('webhook'); }}>Add Webhook</Btn>        :
          tab === 'secrets'   ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('secret');  }}>Add Secret</Btn>         :
          tab === 'externalapps' && !selectedApp ? <Btn variant="primary" icon="Plus" onClick={() => { resetExtForm(); setExtModal('createApp'); }}>Create External App</Btn> : null
        }
      />

      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard label="API Keys"   value={apiKeys.length}   icon="Key"    color="blue"   />
        <StatCard label="OAuth Apps" value={oauthApps.length} icon="Lock"   color="purple" />
        <StatCard label="Webhooks"   value={webhooks.length}  icon="Zap"    color="blue"   />
        <StatCard label="Secrets"    value={secrets.length}   icon="EyeOff" color="amber"  />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={(t) => { setTab(t); if (t !== 'externalapps') setSelectedApp(null); }} />
      <div className="nx-cc-tab-body">

        {tab === 'apikeys' && (
          apiKeys.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Key" size={22} />
              <span style={{ fontWeight: 600 }}>No API keys yet</span>
              <span className="nx-muted nx-mini">Generate API keys to authenticate programmatic access to your organization's data.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('key'); }}>Generate First Key</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Key">Name</Th>
                <Th icon="Code">Key</Th>
                <Th icon="Shield">Scopes</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Calendar">Created</Th>
                <Th end />
              </tr></thead>
              <tbody>{apiKeys.map(k => (
                <tr key={k.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{k.name}</span></td>
                  <td>
                    <code style={{ fontSize: 11, background: 'var(--nx-border)', padding: '2px 6px', borderRadius: 3 }}>{k.keyPrefix || '—'}••••••••</code>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(k.scopes || []).slice(0, 2).map(s => <Pill key={s} tone="gray">{s}</Pill>)}
                      {(k.scopes || []).length > 2 && <span className="nx-muted nx-mini">+{k.scopes.length - 2}</span>}
                    </div>
                  </td>
                  <td><Pill tone="green">{k.status || 'active'}</Pill></td>
                  <td><span className="nx-muted nx-mini">{fmt(k.createdAt)}</span></td>
                  <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                    <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={() => setRotateConfirm(k)}>Rotate</Btn>
                    <Btn variant="ghost" size="sm" icon="X" onClick={() => setDeleteConfirm({ id: k.id, name: k.name, type: 'key' })}>Revoke</Btn>
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'oauthapps' && (
          oauthApps.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Lock" size={22} />
              <span style={{ fontWeight: 600 }}>No OAuth applications yet</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('app'); }}>Create First OAuth App</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Lock">App Name</Th>
                <Th icon="Code">Client ID</Th>
                <Th icon="Shield">Scopes</Th>
                <Th icon="Calendar">Created</Th>
                <Th end />
              </tr></thead>
              <tbody>{oauthApps.map(a => (
                <tr key={a.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{a.name}</span></td>
                  <td><code style={{ fontSize: 11, background: 'var(--nx-border)', padding: '2px 6px', borderRadius: 3 }}>{a.clientId || a.client_id || '—'}</code></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(a.scopes || []).slice(0, 2).map(s => <Pill key={s} tone="gray">{s}</Pill>)}
                      {(a.scopes || []).length > 2 && <span className="nx-muted nx-mini">+{a.scopes.length - 2}</span>}
                    </div>
                  </td>
                  <td><span className="nx-muted nx-mini">{fmt(a.createdAt)}</span></td>
                  <td className="nx-td-end"><Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: a.id, name: a.name, type: 'app' })}>Delete</Btn></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'webhooks' && (
          webhooks.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Zap" size={22} />
              <span style={{ fontWeight: 600 }}>No webhooks configured</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('webhook'); }}>Add First Webhook</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Tag">Name</Th>
                <Th icon="Globe">URL</Th>
                <Th icon="Tag">Events</Th>
                <Th icon="Activity">Status</Th>
                <Th end />
              </tr></thead>
              <tbody>{webhooks.map(w => (
                <tr key={w.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{w.name || '—'}</span></td>
                  <td><code style={{ fontSize: 11 }}>{(w.url || '').length > 40 ? w.url.slice(0, 40) + '…' : w.url}</code></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(w.events || []).slice(0, 2).map(e => <Pill key={e} tone="blue">{e}</Pill>)}
                      {(w.events || []).length > 2 && <span className="nx-muted nx-mini">+{w.events.length - 2}</span>}
                    </div>
                  </td>
                  <td><Pill tone={w.active === false ? 'gray' : 'green'}>{w.active === false ? 'inactive' : w.status || 'active'}</Pill></td>
                  <td className="nx-td-end">
                    <Toggle checked={w.active !== false} onChange={() => toggleWebhookStatus(w)} aria-label={(w.active === false ? 'Enable' : 'Disable') + ' ' + (w.name || w.url)} />
                    <Btn variant="ghost" size="sm" icon="List" onClick={() => viewDeliveries(w)}>Logs</Btn>
                    <Btn variant="ghost" size="sm" icon="Send" onClick={() => testWebhook(w)}>Test</Btn>
                    <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: w.id, name: w.name || w.url, type: 'webhook' })}>Delete</Btn>
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'secrets' && (
          secrets.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="EyeOff" size={22} />
              <span style={{ fontWeight: 600 }}>No secrets stored</span>
              <span className="nx-muted nx-mini">Store API keys, tokens, and credentials securely for use in automation.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('secret'); }}>Add First Secret</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr><Th icon="EyeOff">Name</Th><Th icon="FileText">Description</Th><Th icon="Calendar">Created</Th><Th end /></tr></thead>
              <tbody>{secrets.map(s => (
                <tr key={s.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{s.name}</span></td>
                  <td><span className="nx-muted">{s.description || '—'}</span></td>
                  <td><span className="nx-muted nx-mini">{fmt(s.createdAt)}</span></td>
                  <td className="nx-td-end"><Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: s.id, name: s.name, type: 'secret' })}>Delete</Btn></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {/* ── External Apps — list view ── */}
        {tab === 'externalapps' && !selectedApp && (
          extAppsLoading ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Globe" size={22} />
              <span className="nx-muted nx-mini">Loading external applications…</span>
            </div>
          ) : extApps.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Globe" size={22} />
              <span style={{ fontWeight: 600 }}>No external applications yet</span>
              <span className="nx-muted nx-mini">Create an application to issue API credentials and grant workspace access to external systems.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetExtForm(); setExtModal('createApp'); }}>Create First App</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Globe">Application</Th>
                <Th icon="Key">Credentials</Th>
                <Th icon="Layers">Workspaces</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Calendar">Created</Th>
                <Th end />
              </tr></thead>
              <tbody>{extApps.map(app => (
                <tr key={app.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setSelectedApp(app)}>
                  <td>
                    <div>
                      <div className="nx-row-name-text">{app.name}</div>
                      {app.description && <div className="nx-muted nx-mini" style={{ marginTop: 2 }}>{app.description.length > 60 ? app.description.slice(0, 60) + '…' : app.description}</div>}
                    </div>
                  </td>
                  <td><span className="nx-muted">{app.credentialCount ?? '—'}</span></td>
                  <td><span className="nx-muted">{app.workspaceCount ?? '—'}</span></td>
                  <td><Pill tone={extStatusTone(app.status)}>{app.status}</Pill></td>
                  <td><span className="nx-muted nx-mini">{fmt(app.createdAt)}</span></td>
                  <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                    <Btn variant="ghost" size="sm" icon="ChevronRight" onClick={() => setSelectedApp(app)}>Open</Btn>
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {/* ── External Apps — detail view ── */}
        {tab === 'externalapps' && selectedApp && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <button
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', fontSize: 13, padding: '4px 0' }}
                onClick={() => { setSelectedApp(null); setExtCreds([]); setExtGrants([]); }}
              >
                <NexusIcon name="ChevronLeft" size={14} /> External Apps
              </button>
              <span style={{ color: 'var(--nx-border-strong)', fontSize: 13 }}>/</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedApp.name}</span>
            </div>

            <div className="nx-cc-chart-card" style={{ padding: '16px 20px', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name="Globe" size={18} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedApp.name}</div>
                    {selectedApp.description && <div className="nx-muted" style={{ fontSize: 13, marginTop: 2 }}>{selectedApp.description}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      <Pill tone={extStatusTone(selectedApp.status)}>{selectedApp.status}</Pill>
                      <span className="nx-muted nx-mini">Created {fmt(selectedApp.createdAt)}</span>
                      <span className="nx-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>{selectedApp.id}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {selectedApp.status === 'ACTIVE' && (
                    <Btn variant="ghost" size="sm" icon="PauseCircle" onClick={() => setExtConfirm({ type: 'changeAppStatus', status: 'SUSPENDED', label: 'Suspend this application?' })}>Suspend</Btn>
                  )}
                  {selectedApp.status === 'SUSPENDED' && (
                    <Btn variant="ghost" size="sm" icon="PlayCircle" onClick={() => setExtConfirm({ type: 'changeAppStatus', status: 'ACTIVE', label: 'Reactivate this application?' })}>Reactivate</Btn>
                  )}
                  {selectedApp.status !== 'REVOKED' && (
                    <Btn variant="ghost" size="sm" icon="X" onClick={() => setExtConfirm({ type: 'changeAppStatus', status: 'REVOKED', label: 'Revoke this application?' })}>Revoke</Btn>
                  )}
                </div>
              </div>
            </div>

            {extDetailLoading ? (
              <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '20px 0' }}>
                <NexusIcon name="Loader" size={18} />
                <span className="nx-muted nx-mini">Loading details…</span>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NexusIcon name="Key" size={14} /> API Credentials
                    </div>
                    {selectedApp.status === 'ACTIVE' && (
                      <Btn variant="secondary" size="sm" icon="Plus" onClick={() => { resetExtForm(); setExtModal('createCred'); }}>Add Credential</Btn>
                    )}
                  </div>
                  {extCreds.length === 0 ? (
                    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '20px 0' }}>
                      <NexusIcon name="Key" size={18} />
                      <span className="nx-muted nx-mini">No credentials yet. Add a credential to generate an API key.</span>
                    </div>
                  ) : (
                    <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                      <thead><tr>
                        <Th icon="Key">Name</Th>
                        <Th icon="Code">Key Prefix</Th>
                        <Th icon="Activity">Status</Th>
                        <Th icon="Calendar">Created</Th>
                        <Th icon="Clock">Expires</Th>
                        <Th end />
                      </tr></thead>
                      <tbody>{extCreds.map(cred => (
                        <tr key={cred.id} className="nx-tr">
                          <td><span className="nx-row-name-text">{cred.name}</span></td>
                          <td><code style={{ fontSize: 11, background: 'var(--nx-border)', padding: '2px 6px', borderRadius: 3 }}>{cred.keyPrefix || '—'}••••••••</code></td>
                          <td><Pill tone={cred.status === 'ACTIVE' ? 'green' : 'red'}>{cred.status}</Pill></td>
                          <td><span className="nx-muted nx-mini">{fmt(cred.createdAt)}</span></td>
                          <td><span className="nx-muted nx-mini">{cred.expiresAt ? fmt(cred.expiresAt) : 'Never'}</span></td>
                          <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                            {cred.status === 'ACTIVE' && (
                              <Btn variant="ghost" size="sm" icon="X" onClick={() => setExtConfirm({ type: 'revokeCredential', id: cred.id, name: cred.name, label: 'Revoke credential "' + cred.name + '"?' })}>Revoke</Btn>
                            )}
                          </td>
                        </tr>
                      ))}</tbody>
                    </table></div></div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NexusIcon name="Layers" size={14} /> Workspace Access
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <a
                        href="/api/v1/external/docs"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--nx-accent)', textDecoration: 'none', fontWeight: 500 }}
                      >
                        <NexusIcon name="BookOpen" size={12} /> Open API Docs
                      </a>
                      {selectedApp.status === 'ACTIVE' && (
                        <Btn variant="secondary" size="sm" icon="Plus" onClick={() => { resetExtForm(); setExtModal('grantWs'); }}>Grant Workspace</Btn>
                      )}
                    </div>
                  </div>
                  {extGrants.length === 0 ? (
                    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '20px 0' }}>
                      <NexusIcon name="Layers" size={18} />
                      <span className="nx-muted nx-mini">No workspace access granted. Grant access to allow this application to interact with a workspace.</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {extGrants.map(grant => {
                        const wsName = grant.workspace?.name || orgWorkspaces.find(w => w.id === grant.workspaceId)?.name || grant.workspaceId;
                        const wsStatus = grant.workspace?.status || orgWorkspaces.find(w => w.id === grant.workspaceId)?.status;
                        const wsStatusTone = wsStatus === 'ACTIVE' ? 'green' : wsStatus === 'SUSPENDED' ? 'amber' : wsStatus ? 'red' : 'gray';
                        return (
                          <div key={grant.id} className="nx-cc-chart-card" style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Workspace name + status */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                  <span style={{ fontWeight: 600, fontSize: 13 }}>{wsName}</span>
                                  <Pill tone={wsStatusTone}>{wsStatus || '—'}</Pill>
                                  <Pill tone={grant.status === 'ACTIVE' ? 'green' : 'red'} style={{ marginLeft: 0 }}>Grant: {grant.status}</Pill>
                                </div>

                                {/* Workspace ID row */}
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Workspace ID</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <code style={{ fontSize: 11, background: 'var(--nx-border)', padding: '3px 8px', borderRadius: 4, fontFamily: 'monospace', color: 'var(--nx-text)', letterSpacing: '0.02em', flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                                      {grant.workspaceId}
                                    </code>
                                    <button
                                      type="button"
                                      title="Copy Workspace ID"
                                      aria-label={'Copy Workspace ID for ' + wsName}
                                      onClick={() => {
                                        navigator.clipboard?.writeText(grant.workspaceId).then(
                                          () => toast({ tone: 'success', icon: 'Check', title: 'Copied', desc: 'Workspace ID copied to clipboard.' }),
                                          () => toast({ tone: 'error', icon: 'AlertCircle', title: 'Copy failed', desc: 'Could not access clipboard. Please copy the ID manually.' }),
                                        );
                                      }}
                                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid var(--nx-border)', borderRadius: 5, cursor: 'pointer', color: 'var(--nx-text-muted)', padding: '3px 8px', fontSize: 11, flexShrink: 0 }}
                                    >
                                      <NexusIcon name="Copy" size={11} /> Copy
                                    </button>
                                  </div>
                                </div>

                                {/* Scopes */}
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Scopes</div>
                                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {(grant.scopes || []).map(s => <Pill key={s} tone="blue">{s}</Pill>)}
                                    {(grant.scopes || []).length === 0 && <span className="nx-muted nx-mini">None</span>}
                                  </div>
                                </div>

                                {/* Granted date */}
                                <div style={{ marginTop: 8 }}>
                                  <span className="nx-muted nx-mini">Granted {fmt(grant.createdAt)}</span>
                                </div>
                              </div>

                              {/* Actions */}
                              {grant.status === 'ACTIVE' && (
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                  <Btn variant="ghost" size="sm" icon="Edit" onClick={() => { setEditScopesGrant(grant); setExtForm(f => ({ ...f, scopes: grant.scopes || [] })); }}>Edit Scopes</Btn>
                                  <Btn variant="ghost" size="sm" icon="X" onClick={() => setExtConfirm({ type: 'revokeGrant', workspaceId: grant.workspaceId, name: wsName, label: 'Revoke access for "' + wsName + '"?' })}>Revoke</Btn>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'integrations' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: 14 }}>
            {INTEGRATIONS.map(int => (
              <div key={int.id} className="nx-cc-chart-card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NexusIcon name={int.icon} size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{int.name}</div>
                    <Pill tone={int.status === 'available' ? 'green' : 'gray'}>{int.status === 'available' ? 'Available' : 'Coming Soon'}</Pill>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 10 }}>{int.desc}</div>
                <Btn variant={int.status === 'available' ? 'secondary' : 'ghost'} size="sm" style={{ width: '100%' }}
                  disabled={int.status !== 'available'}
                  onClick={() => toast({ tone: 'info', icon: 'Plug', title: 'Connect ' + int.name, desc: 'Integration setup coming soon.' })}>
                  {int.status === 'available' ? 'Connect' : 'Coming Soon'}
                </Btn>
              </div>
            ))}
          </div>
        )}

        {tab === 'ratelimits' && (
          <div>
            <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--nx-text-muted)' }}>Default rate limits for your organization tier. Contact support to request increases.</div>
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Gauge">Tier</Th>
                <Th icon="Activity">Limit</Th>
                <Th icon="Zap">Burst</Th>
                <Th icon="Code">Methods</Th>
              </tr></thead>
              <tbody>{RATE_LIMITS_STATIC.map(r => (
                <tr key={r.tier} className="nx-tr">
                  <td><span style={{ fontWeight: 600 }}>{r.tier}</span></td>
                  <td><span className="nx-muted">{r.limit}</span></td>
                  <td><span className="nx-muted">{r.burst}</span></td>
                  <td><code style={{ fontSize: 11 }}>{r.methods}</code></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          </div>
        )}

        {tab === 'logs' && (
          logs.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="FileText" size={22} />
              <span style={{ fontWeight: 600 }}>API Logs</span>
              <span className="nx-muted nx-mini">No request logs available yet. Logs appear here as your API keys are used.</span>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Calendar">Time</Th>
                <Th icon="Code">Method</Th>
                <Th icon="Globe">Path</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Clock">Latency</Th>
              </tr></thead>
              <tbody>{logs.map((l, i) => (
                <tr key={l.id || i} className="nx-tr">
                  <td><span className="nx-muted nx-mini">{fmt(l.createdAt)}</span></td>
                  <td><code style={{ fontSize: 11 }}>{l.method}</code></td>
                  <td><code style={{ fontSize: 11 }}>{l.path}</code></td>
                  <td><Pill tone={l.status >= 400 ? 'red' : 'green'}>{l.status}</Pill></td>
                  <td><span className="nx-muted">{l.latencyMs}ms</span></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'sdk' && (
          /* C-06: SDK and documentation are not yet published. Replaced clickable
             stub cards (which showed "coming soon" toast) with an honest state. */
          <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '48px 0' }}>
            <NexusIcon name="Code" size={22} />
            <span style={{ fontWeight: 600 }}>SDK &amp; Documentation — Coming Soon</span>
            <span className="nx-muted nx-mini">Client libraries, OpenAPI spec, and Postman collection are being prepared.</span>
            <span className="nx-muted nx-mini">Contact your account manager or platform administrator for early access.</span>
          </div>
        )}
      </div>

      {/* ── Existing modals ── */}

      {createModal === 'key' && (
        <TenancyModal title="Generate API Key" icon="Key" onClose={() => { setCreateModal(null); resetForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Key Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Production Integration" />
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Scopes</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SCOPES.map(s => (
                  <button key={s} onClick={() => toggleScope(s)} className={'nx-chip' + (form.scopes.includes(s) ? ' is-active' : '')}>
                    <NexusIcon name={form.scopes.includes(s) ? 'Check' : 'Plus'} size={10} />{s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Key" disabled={!form.name.trim()} onClick={handleCreateKey}>Generate Key</Btn>
          </div>
        </TenancyModal>
      )}

      {createModal === 'app' && (
        <TenancyModal title="Create OAuth Application" icon="Lock" onClose={() => { setCreateModal(null); resetForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>App Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. My Integration App" />
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Scopes</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SCOPES.map(s => (
                  <button key={s} onClick={() => toggleScope(s)} className={'nx-chip' + (form.scopes.includes(s) ? ' is-active' : '')}>
                    <NexusIcon name={form.scopes.includes(s) ? 'Check' : 'Plus'} size={10} />{s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim()} onClick={handleCreateApp}>Create App</Btn>
          </div>
        </TenancyModal>
      )}

      {createModal === 'webhook' && (
        <TenancyModal title="Add Webhook" icon="Zap" onClose={() => { setCreateModal(null); resetForm(); }} width={520}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Webhook Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Order Events" />
            </div>
            <div className="nx-form-row">
              <label>Endpoint URL *</label>
              <input className="nx-input" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://your-server.com/webhook" />
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Events</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {WH_EVENTS.map(ev => (
                  <button key={ev} onClick={() => toggleEvent(ev)} className={'nx-chip' + (form.events.includes(ev) ? ' is-active' : '')}>
                    <NexusIcon name={form.events.includes(ev) ? 'Check' : 'Plus'} size={10} />{ev}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim() || !form.url.trim()} onClick={handleCreateWebhook}>Add Webhook</Btn>
          </div>
        </TenancyModal>
      )}

      {createModal === 'secret' && (
        <TenancyModal title="Add Secret" icon="EyeOff" onClose={() => { setCreateModal(null); resetForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Secret Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. OPENAI_API_KEY" />
            </div>
            <div className="nx-form-row">
              <label>Value *</label>
              <input className="nx-input" type="password" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="Secret value" />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <input className="nx-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim() || !form.value.trim()} onClick={handleCreateSecret}>Store Secret</Btn>
          </div>
        </TenancyModal>
      )}

      {newKeyValue && (
        <TenancyModal title="API Key Generated" icon="Key" onClose={() => setNewKeyValue(null)}>
          <div style={{ marginTop: 12 }}>
            <div style={{ background: 'var(--nx-success-tint)', border: '1px solid var(--nx-success)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--nx-text)', fontWeight: 500 }}>
              <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning)', marginRight: 6 }} />
              Copy this key now. It will not be shown again.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, fontSize: 11, background: 'var(--nx-border)', padding: '8px 10px', borderRadius: 4, wordBreak: 'break-all' }}>{newKeyValue}</code>
              <Btn variant="ghost" size="sm" icon="Copy" onClick={() => { navigator.clipboard?.writeText(newKeyValue); toast({ tone: 'success', icon: 'Copy', title: 'Copied to clipboard' }); }}>Copy</Btn>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={() => setNewKeyValue(null)}>I've copied it</Btn>
          </div>
        </TenancyModal>
      )}

      {rotateConfirm && (
        <TenancyModal title={'Rotate API Key — ' + rotateConfirm.name + '?'} icon="RefreshCw" danger onClose={() => setRotateConfirm(null)}>
          <p className="nx-confirm-desc">Rotating this key will immediately invalidate the current key. All integrations using it will stop working until updated with the new key.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRotateConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="RefreshCw" onClick={() => { handleRotate(rotateConfirm); setRotateConfirm(null); }}>Rotate Key</Btn>
          </div>
        </TenancyModal>
      )}

      {deleteConfirm && (
        <TenancyModal title={(deleteConfirm.type === 'key' || deleteConfirm.type === 'secret' ? 'Revoke' : 'Delete') + ' ' + deleteConfirm.name + '?'} icon="X" danger onClose={() => setDeleteConfirm(null)}>
          <p className="nx-confirm-desc">This cannot be undone. Any integrations using this {deleteConfirm.type} will stop working immediately.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={handleRevoke}>{deleteConfirm.type === 'key' || deleteConfirm.type === 'secret' ? 'Revoke' : 'Delete'}</Btn>
          </div>
        </TenancyModal>
      )}

      {newAppCreds && (
        <TenancyModal title="OAuth App Created" icon="Lock" onClose={() => setNewAppCreds(null)}>
          <div style={{ marginTop: 12 }}>
            <div style={{ background: 'var(--nx-success-tint)', border: '1px solid var(--nx-success)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 500 }}>
              <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning)', marginRight: 6 }} />
              Copy the client secret now. It will not be shown again.
            </div>
            <div className="nx-form" style={{ gap: 10 }}>
              <div className="nx-form-row">
                <label>Client ID</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <code style={{ flex: 1, fontSize: 11, background: 'var(--nx-border)', padding: '8px 10px', borderRadius: 4, wordBreak: 'break-all' }}>{newAppCreds.clientId}</code>
                  <Btn variant="ghost" size="sm" icon="Copy" onClick={() => { navigator.clipboard?.writeText(newAppCreds.clientId); toast({ tone: 'success', icon: 'Copy', title: 'Client ID copied' }); }}>Copy</Btn>
                </div>
              </div>
              <div className="nx-form-row">
                <label>Client Secret</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <code style={{ flex: 1, fontSize: 11, background: 'var(--nx-border)', padding: '8px 10px', borderRadius: 4, wordBreak: 'break-all' }}>{newAppCreds.clientSecret}</code>
                  <Btn variant="ghost" size="sm" icon="Copy" onClick={() => { navigator.clipboard?.writeText(newAppCreds.clientSecret); toast({ tone: 'success', icon: 'Copy', title: 'Client secret copied' }); }}>Copy</Btn>
                </div>
              </div>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={() => setNewAppCreds(null)}>I've copied the credentials</Btn>
          </div>
        </TenancyModal>
      )}

      {whDeliveries && (
        <TenancyModal title={'Delivery Logs — ' + (whDeliveries.webhook.name || whDeliveries.webhook.url || 'Webhook')} icon="Zap" onClose={() => setWhDeliveries(null)} width={600}>
          {whDeliveries.logs.length === 0 ? (
            <div className="nx-empty" style={{ padding: '20px 0', flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Inbox" size={20} />
              <span className="nx-muted nx-mini">No delivery attempts yet.</span>
            </div>
          ) : (
            <div className="nx-table-wrap" style={{ marginTop: 12 }}><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Calendar">Time</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Code">HTTP</Th>
                <Th icon="Tag">Event</Th>
              </tr></thead>
              <tbody>{whDeliveries.logs.map((dl, i) => (
                <tr key={dl.id || i} className="nx-tr">
                  <td><span className="nx-muted nx-mini">{fmt(dl.createdAt || dl.deliveredAt)}</span></td>
                  <td><Pill tone={(dl.success || dl.status === 'success') ? 'green' : 'red'}>{(dl.success || dl.status === 'success') ? 'Success' : 'Failed'}</Pill></td>
                  <td><span className="nx-muted">{dl.httpStatus || dl.statusCode || '—'}</span></td>
                  <td><code style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{(dl.event || dl.eventType || '—').slice(0, 40)}</code></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )}
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={() => setWhDeliveries(null)}>Close</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── External App modals ── */}

      {extModal === 'createApp' && (
        <TenancyModal title="Create External Application" icon="Globe" onClose={() => { setExtModal(null); resetExtForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Application Name *</label>
              <input className="nx-input" autoFocus value={extForm.name} onChange={e => setExtForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sync Bot, Reporting Pipeline" />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <input className="nx-input" value={extForm.description} onChange={e => setExtForm(f => ({ ...f, description: e.target.value }))} placeholder="What does this application do?" />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setExtModal(null); resetExtForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Globe" disabled={!extForm.name.trim()} onClick={handleCreateExtApp}>Create Application</Btn>
          </div>
        </TenancyModal>
      )}

      {extModal === 'createCred' && (
        <TenancyModal title="Add API Credential" icon="Key" onClose={() => { setExtModal(null); resetExtForm(); }} width={480}>
          <div className="nx-form" style={{ gap: 12, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Credential Name *</label>
              <input className="nx-input" autoFocus value={extForm.credName} onChange={e => setExtForm(f => ({ ...f, credName: e.target.value }))} placeholder="e.g. Production Key, Staging Key" />
            </div>
            <div className="nx-form-row">
              <label>Expires</label>
              <ExpiryDatePicker value={extForm.credExpiry} onChange={v => setExtForm(f => ({ ...f, credExpiry: v }))} />
              <span className="nx-muted nx-mini" style={{ marginTop: 3 }}>Leave blank for no expiry.</span>
            </div>
          </div>
          <div style={{ background: 'var(--nx-warning-tint)', border: '1px solid var(--nx-warning)', borderRadius: 6, padding: '10px 14px', margin: '12px 0', fontSize: 12 }}>
            <NexusIcon name="AlertTriangle" size={13} style={{ color: 'var(--nx-warning)', marginRight: 6 }} />
            The API key will be shown once immediately after creation and cannot be retrieved again.
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setExtModal(null); resetExtForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Key" disabled={!extForm.credName.trim()} onClick={handleCreateExtCred}>Create Credential</Btn>
          </div>
        </TenancyModal>
      )}

      {extModal === 'grantWs' && (
        <TenancyModal title="Grant Workspace Access" icon="Layers" onClose={() => { setExtModal(null); resetExtForm(); }} width={520}>
          <div className="nx-form" style={{ gap: 12, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Workspace *</label>
              <select className="nx-input" value={extForm.wsId} onChange={e => setExtForm(f => ({ ...f, wsId: e.target.value }))}>
                <option value="">Select a workspace…</option>
                {orgWorkspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>{ws.name}</option>
                ))}
              </select>
              {orgWorkspaces.length === 0 && <span className="nx-muted nx-mini" style={{ marginTop: 3 }}>No active workspaces found in this organization.</span>}
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Scopes *</label>
              <ScopeCheckboxes selected={extForm.scopes} onToggle={toggleExtScope} />
              {extForm.scopes.length === 0 && <span className="nx-muted nx-mini" style={{ marginTop: 4 }}>Select at least one scope.</span>}
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setExtModal(null); resetExtForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!extForm.wsId || extForm.scopes.length === 0} onClick={handleGrantWorkspace}>Grant Access</Btn>
          </div>
        </TenancyModal>
      )}

      {editScopesGrant && (
        <TenancyModal title="Edit Scopes" icon="Shield" onClose={() => { setEditScopesGrant(null); resetExtForm(); }} width={520}>
          <div className="nx-form" style={{ gap: 12, marginTop: 8 }}>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Scopes *</label>
              <ScopeCheckboxes selected={extForm.scopes} onToggle={toggleExtScope} />
              {extForm.scopes.length === 0 && <span className="nx-muted nx-mini" style={{ marginTop: 4 }}>Select at least one scope.</span>}
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setEditScopesGrant(null); resetExtForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={extForm.scopes.length === 0} onClick={handleUpdateScopes}>Save Scopes</Btn>
          </div>
        </TenancyModal>
      )}

      {newExtApiKey && (
        <TenancyModal title="API Key Created" icon="Key" onClose={() => setNewExtApiKey(null)}>
          <div style={{ marginTop: 12 }}>
            <div style={{ background: 'var(--nx-warning-tint)', border: '1px solid var(--nx-warning)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 500 }}>
              <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning)', marginRight: 6 }} />
              Copy this key now. It will not be shown again.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, fontSize: 11, background: 'var(--nx-border)', padding: '8px 10px', borderRadius: 4, wordBreak: 'break-all' }}>{newExtApiKey}</code>
              <Btn variant="ghost" size="sm" icon="Copy" onClick={() => {
                navigator.clipboard?.writeText(newExtApiKey)
                  .then(() => toast({ tone: 'success', icon: 'Copy', title: 'Copied to clipboard' }))
                  .catch(() => toast({ tone: 'error', icon: 'AlertCircle', title: 'Copy failed', desc: 'Select and copy the key text manually.' }));
              }}>Copy</Btn>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={() => setNewExtApiKey(null)}>I've copied it</Btn>
          </div>
        </TenancyModal>
      )}

      {extConfirm && (
        <TenancyModal title={extConfirm.label || 'Confirm action?'} icon="AlertTriangle" danger onClose={() => setExtConfirm(null)}>
          <p className="nx-confirm-desc">
            {extConfirm.type === 'revokeCredential' && `Revoking "${extConfirm.name}" will immediately invalidate its API key. Any integrations using it will stop working.`}
            {extConfirm.type === 'revokeGrant' && `Revoking access for "${extConfirm.name}" will prevent this application from accessing that workspace's data.`}
            {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'SUSPENDED' && 'Suspending this application will block all API calls using its credentials until reactivated.'}
            {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'REVOKED' && 'Revoking this application is permanent. All credentials and workspace grants will be deactivated and cannot be restored.'}
            {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'ACTIVE' && 'Reactivating this application will restore API access for its active credentials.'}
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setExtConfirm(null)}>Cancel</Btn>
            <Btn
              variant={extConfirm.type === 'changeAppStatus' && extConfirm.status === 'ACTIVE' ? 'primary' : 'danger'}
              icon={extConfirm.type === 'changeAppStatus' && extConfirm.status === 'ACTIVE' ? 'Check' : 'X'}
              onClick={handleExtConfirm}
            >
              {extConfirm.type === 'revokeCredential' && 'Revoke Credential'}
              {extConfirm.type === 'revokeGrant' && 'Revoke Access'}
              {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'SUSPENDED' && 'Suspend App'}
              {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'REVOKED' && 'Revoke App'}
              {extConfirm.type === 'changeAppStatus' && extConfirm.status === 'ACTIVE' && 'Reactivate App'}
            </Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
